
import React from 'react';
import { ExternalLink, Edit, Trash2 } from "lucide-react";
import UnitToggle from '@/components/UnitToggle';
import { UnitSystem } from '@/utils/unitConversion/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import RecipeIcon from '@/components/common/RecipeIcon';

interface RecipeHeaderProps {
  sourceUrl?: string;
  preferredUnits: UnitSystem;
  onUnitToggle: (value: UnitSystem) => void;
  tags?: string[];
  isQuickRecipe: boolean;
  isComposition?: boolean;
  servings?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

const RecipeHeader: React.FC<RecipeHeaderProps> = ({ 
  sourceUrl, 
  preferredUnits, 
  onUnitToggle,
  tags,
  isQuickRecipe,
  isComposition = false,
  servings,
  canEdit = false,
  canDelete = false,
  onEdit,
  onDelete,
}) => {
  // Extract domain name from source URL
  const getSourceDomain = (url: string) => {
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      return domain;
    } catch (e) {
      return 'external source';
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4 mb-4">
        {(isQuickRecipe || isComposition || (isQuickRecipe && servings)) && (
          <div className="flex items-center gap-2">
            {isQuickRecipe && (
              <Tooltip>
                <TooltipTrigger>
                  <RecipeIcon type="quick-recipe" size={20} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Quick Recipe</p>
                </TooltipContent>
              </Tooltip>
            )}
            {isComposition && (
              <Tooltip>
                <TooltipTrigger>
                  <RecipeIcon type="composition" size={20} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Meal Composition</p>
                </TooltipContent>
              </Tooltip>
            )}
            {isQuickRecipe && servings && (
              <span className="text-sm text-muted-foreground">
                Servings: {servings}
              </span>
            )}
          </div>
        )}
        
        {/* All screen sizes: metric/imperial left, edit/delete right */}
        <div className="flex items-center justify-between sm:justify-start sm:gap-4 md:justify-between">
          <div className="flex items-center gap-2">
            <UnitToggle value={preferredUnits} onValueChange={onUnitToggle} />
          </div>
          {(canEdit || canDelete) && (
            <div className="flex items-center gap-2 sm:order-2 md:order-2">
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEdit}
                >
                  <Edit size={16} className="mr-2" />
                  Edit
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDelete}
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {sourceUrl && (
        <a 
          href={sourceUrl} 
          target="_blank" 
          rel="noreferrer"
          className="text-sm text-blue-500 hover:text-blue-700 flex items-center gap-1 mb-4"
        >
          Recipe imported from <span className="font-medium">{getSourceDomain(sourceUrl)}</span> <ExternalLink size={14} />
        </a>
      )}

      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {tags.map((tag, index) => (
            <Badge key={index} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      )}
    </>
  );
};

export default RecipeHeader;
